/**
 * Global Ctrl+K / Cmd+K CommandPalette.
 *
 * Sources:
 *  1. Разделы навигации — мгновенно из переданного prop navItems[]
 *  2. Партнёры / Сделки / Встречи — серверный fuzzy-поиск (pg_trgm)
 *
 * navItems = [{ to, label, icon: LucideComponent, section }] — видимые разделы,
 * уже отфильтрованные Layout'ом через isModuleVisible.
 *
 * Keyboard: Ctrl+K/Cmd+K — открыть/закрыть | ESC — закрыть | ↑↓ — навигация | Enter — перейти
 */
import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Loader2, Handshake, FileText, Calendar, ArrowRight, X, LayoutDashboard } from 'lucide-react'
import { partnerCrm } from '../../api/partnerCrm'
import { searchNavigation } from '../layout/navigationSearch'
import { getNavigationDisplayItem } from '../layout/workspaceNavigationModel'

const DEBOUNCE_MS = 250
const MAX_NAV_RESULTS = 8

export default function CommandPalette({ open, onClose, navItems = [] }) {
    const [query, setQuery] = useState('')
    const [serverResults, setServerResults] = useState(null)
    const [loading, setLoading] = useState(false)
    const [selectedIdx, setSelectedIdx] = useState(0)
    const inputRef = useRef(null)
    const navigate = useNavigate()
    const debounceRef = useRef(null)

    // ── Фильтрация разделов навигации (мгновенно, без сервера) ───────────
    const navResults = useMemo(() => {
        const q = query.trim()
        if (!q || q.length < 2) return []
        return searchNavigation(navItems.map(getNavigationDisplayItem), q, MAX_NAV_RESULTS)
    }, [query, navItems])

    // ── Flat list для keyboard-навигации ─────────────────────────────────
    const flatItems = useMemo(() => {
        const items = navResults.map(n => ({ type: 'nav', ...n }))
        if (serverResults) {
            for (const p of serverResults.partners || []) items.push({ type: 'partner', id: p.id, ...p })
            for (const d of serverResults.deals || []) items.push({ type: 'deal', id: d.id, ...d })
            for (const m of serverResults.meetings || []) items.push({ type: 'meeting', id: m.id, ...m })
        }
        return items
    }, [navResults, serverResults])

    // ── Серверный поиск партнёров/сделок/встреч (debounced) ──────────────
    const runServerSearch = useCallback(async (term) => {
        if (!term || term.trim().length < 2) { setServerResults(null); return }
        setLoading(true)
        try {
            const data = await partnerCrm.crossSearch(term.trim())
            setServerResults(data)
        } catch (e) {
            console.error('[CommandPalette] server search failed', e)
            setServerResults(null)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => runServerSearch(query), DEBOUNCE_MS)
        return () => debounceRef.current && clearTimeout(debounceRef.current)
    }, [query, runServerSearch])

    useEffect(() => {
        if (open && inputRef.current) { inputRef.current.focus(); inputRef.current.select?.() }
    }, [open])

    useEffect(() => {
        if (!open) { setQuery(''); setServerResults(null); setSelectedIdx(0) }
    }, [open])

    useEffect(() => { setSelectedIdx(0) }, [query])

    const handleNavigate = useCallback((item) => {
        if (!item) return
        if (item.type === 'nav') navigate(`/${item.to}`)
        else if (item.type === 'partner') navigate(`/partner-crm/${item.id}`)
        else if (item.type === 'deal') navigate(`/partner-crm/${item.partner_id}?tab=deals`)
        else if (item.type === 'meeting') navigate(`/partner-crm/${item.partner_id}?tab=meetings`)
        onClose()
    }, [navigate, onClose])

    const handleKey = useCallback((e) => {
        if (e.key === 'Escape') { e.preventDefault(); onClose(); return }
        if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx((i) => Math.min(i + 1, flatItems.length - 1)) }
        if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx((i) => Math.max(i - 1, 0)) }
        if (e.key === 'Enter') { e.preventDefault(); handleNavigate(flatItems[selectedIdx]) }
    }, [flatItems, selectedIdx, onClose, handleNavigate])

    if (!open) return null

    const serverList = serverResults
        ? [...(serverResults.partners || []).map(p => ({ type: 'partner', id: p.id, ...p })),
           ...(serverResults.deals || []).map(d => ({ type: 'deal', id: d.id, ...d })),
           ...(serverResults.meetings || []).map(m => ({ type: 'meeting', id: m.id, ...m }))]
        : []
    const hasNav = navResults.length > 0
    const hasServer = serverList.length > 0
    const hasAny = hasNav || hasServer
    const isEmpty = query.trim().length >= 2 && !loading && !hasAny

    return (
        <div
            onClick={onClose}
            className="modal-overlay"
            style={{ alignItems: 'flex-start', paddingTop: '10vh' }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                onKeyDown={handleKey}
                className="bg-surface radius-lg border border-border"
                style={{ width: '90%', maxWidth: 640, maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}
            >
                {/* Поисковая строка */}
                <div className="flex items-center gap-10 p-12 border-b">
                    <Search size={16} className="text-secondary" />
                    <input
                        ref={inputRef}
                        type="text"
                        className="text-md flex-1 bg-transparent border-none outline-none"
                        placeholder="Найти раздел, партнёра, сделку…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    {loading && <Loader2 size={14} className="animate-spin text-secondary" />}
                    <kbd className="text-xs px-6 py-2 radius-sm text-secondary"
                        style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)' }}>
                        ESC
                    </kbd>
                    <button onClick={onClose} className="bg-transparent border-none cursor-pointer text-secondary">
                        <X size={16} />
                    </button>
                </div>

                {/* Результаты */}
                <div style={{ overflowY: 'auto', flex: 1, padding: 8 }}>
                    {query.trim().length < 2 && (
                        <div className="p-20 text-center text-secondary text-sm">
                            Введите 2+ символа — разделы находятся мгновенно
                        </div>
                    )}
                    {isEmpty && (
                        <div className="p-20 text-center text-secondary text-sm">
                            Ничего не найдено по «{query.trim()}»
                        </div>
                    )}

                    {hasAny && (
                        <div className="flex flex-col gap-2">
                            {/* Разделы навигации */}
                            {hasNav && (
                                <>
                                    <div className="cmd-palette__group-label">Разделы</div>
                                    {navResults.map((item, idx) => {
                                        const isSel = idx === selectedIdx
                                        const Icon = item.icon || LayoutDashboard
                                        return (
                                            <button key={`nav-${item.to}`}
                                                onMouseEnter={() => setSelectedIdx(idx)}
                                                onClick={() => handleNavigate({ type: 'nav', ...item })}
                                                className="text-left radius-sm border border-transparent cursor-pointer flex items-center gap-10"
                                                style={{
                                                    padding: '8px 10px',
                                                    background: isSel ? 'var(--color-accent-bg)' : 'transparent',
                                                    borderColor: isSel ? 'var(--color-accent)' : 'transparent',
                                                }}>
                                                <Icon size={14} style={{ color: isSel ? 'var(--color-accent)' : 'var(--color-text-secondary)', flexShrink: 0 }} />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm fw-500">{item.label}</div>
                                                    {item.section && <div className="text-xs text-secondary">{item.section}</div>}
                                                </div>
                                                <ArrowRight size={12} className="text-secondary" style={{ flexShrink: 0 }} />
                                            </button>
                                        )
                                    })}
                                </>
                            )}

                            {/* Партнёры / Сделки / Встречи */}
                            {hasServer && (
                                <>
                                    <div className="cmd-palette__group-label" style={{ marginTop: hasNav ? 8 : 0 }}>
                                        Партнёры и сделки
                                    </div>
                                    {serverList.map((item, idx) => {
                                        const globalIdx = navResults.length + idx
                                        const isSel = globalIdx === selectedIdx
                                        return (
                                            <button key={`${item.type}-${item.id}`}
                                                onMouseEnter={() => setSelectedIdx(globalIdx)}
                                                onClick={() => handleNavigate(item)}
                                                className="text-left radius-sm border border-transparent cursor-pointer flex items-center gap-10"
                                                style={{
                                                    padding: '8px 10px',
                                                    background: isSel ? 'var(--color-accent-bg)' : 'transparent',
                                                    borderColor: isSel ? 'var(--color-accent)' : 'transparent',
                                                }}>
                                                <RowIcon type={item.type} />
                                                <div className="flex-1 min-w-0">
                                                    <RowTitle item={item} />
                                                    <RowSubtitle item={item} />
                                                </div>
                                                <ArrowRight size={12} className="text-secondary" style={{ flexShrink: 0 }} />
                                            </button>
                                        )
                                    })}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-8-12 border-t border-border flex items-center gap-12 text-xs text-secondary">
                    <span>↑↓ навигация</span>
                    <span>Enter — перейти</span>
                    <span style={{ marginLeft: 'auto' }}>
                        {flatItems.length > 0 ? `${flatItems.length} результатов` : ''}
                    </span>
                </div>
            </div>
        </div>
    )
}

function RowIcon({ type }) {
    if (type === 'partner') return <Handshake size={14} className="text-accent" />
    if (type === 'deal') return <FileText size={14} className="text-info" />
    if (type === 'meeting') return <Calendar size={14} className="text-success" />
    return null
}

function RowTitle({ item }) {
    if (item.type === 'partner') {
        const fi = item.ref_code || (item.partner_number != null ? `fi${item.partner_number}` : null)
        const name = item.name || item.display_name || ''
        return (
            <div className="text-sm fw-500">
                {name}
                {fi && !name.toLowerCase().includes(fi.toLowerCase()) && (
                    <span className="text-secondary ml-6 text-xs font-mono">({fi})</span>
                )}
            </div>
        )
    }
    if (item.type === 'deal') return (
        <div className="text-sm fw-500">
            {item.deal_number ? `#${item.deal_number}` : 'Сделка'}
            {item.host_order_id != null && <span className="text-secondary ml-6 text-xs">PS2 #{item.host_order_id}</span>}
        </div>
    )
    if (item.type === 'meeting') return (
        <div className="text-sm fw-500">
            {item.contact_person_name}
            {item.sequence_number != null && <span className="text-secondary ml-6 text-xs">№{item.sequence_number}</span>}
        </div>
    )
    return null
}

function RowSubtitle({ item }) {
    if (item.type === 'partner') {
        const parts = []
        if (item.ref_code) parts.push(`ref: ${item.ref_code}`)
        if (item.city) parts.push(item.city)
        if (item.phone) parts.push(item.phone)
        return <div className="text-xs text-secondary">{parts.join(' · ') || 'Партнёр'}</div>
    }
    if (item.type === 'deal') {
        const pFi = item.partner_ref_code || (item.partner_number != null ? `fi${item.partner_number}` : null)
        const pName = pFi && !item.partner_name?.toLowerCase().includes(pFi.toLowerCase())
            ? `${item.partner_name} (${pFi})`
            : (item.partner_name || 'Партнёр')
        return <div className="text-xs text-secondary">{pName} · {item.status}</div>
    }
    if (item.type === 'meeting') {
        const pFi = item.partner_ref_code || (item.partner_number != null ? `fi${item.partner_number}` : null)
        const pName = pFi && !item.partner_name?.toLowerCase().includes(pFi.toLowerCase())
            ? `${item.partner_name} (${pFi})`
            : (item.partner_name || 'Партнёр')
        return (
            <div className="text-xs text-secondary">
                {pName}
                {item.meeting_date && ` · ${new Date(item.meeting_date).toLocaleDateString('ru-RU')}`}
            </div>
        )
    }
    return null
}
